import type { GraphEdge } from '../graph/nodeDefs.js';
import type { PendingConnection } from '../editor/connectionMutations.js';

interface ConnectionStateDeps {
  getEdges: () => GraphEdge[];
  resolvePendingConnection: (
    nodeId: string,
    portIdx: number,
    io: 'in' | 'out',
    pendingConn: PendingConnection | null,
    clearPending: () => void,
  ) => { connected: boolean; typeMismatch: boolean };
  scheduleRender: () => void;
}

export interface ConnectionStateController {
  readonly pendingConn: PendingConnection | null;
  setPendingConn(next: PendingConnection | null): void;
  clearPendingConn(): void;
  getVisibleEdges(): GraphEdge[];
  cancelPendingConn(): boolean;
  completePendingConnection(nodeId: string, portIdx: number, io: 'in' | 'out'): PendingConnectionResult;
}

export interface PendingConnectionResult {
  connected: boolean;
  typeMismatch: boolean;
}

export function createConnectionStateController(deps: ConnectionStateDeps): ConnectionStateController {
  let currentPendingConn: PendingConnection | null = null;

  const clearPendingConn = (): void => {
    currentPendingConn = null;
  };

  return {
    get pendingConn(): PendingConnection | null {
      return currentPendingConn;
    },
    setPendingConn(next: PendingConnection | null): void {
      currentPendingConn = next;
    },
    clearPendingConn,
    getVisibleEdges(): GraphEdge[] {
      const suppressed = currentPendingConn?.restoreEdge;
      const edges = deps.getEdges();
      if (!suppressed) return edges;
      return edges.filter(edge => !isSameEdge(edge, suppressed));
    },
    cancelPendingConn(): boolean {
      const hadRestoreEdge = !!(currentPendingConn?.restoreOnCancel && currentPendingConn.restoreEdge);
      currentPendingConn = null;
      if (hadRestoreEdge) {
        deps.scheduleRender();
        return true;
      }
      return false;
    },
    completePendingConnection(nodeId: string, portIdx: number, io: 'in' | 'out'): PendingConnectionResult {
      const result = deps.resolvePendingConnection(nodeId, portIdx, io, currentPendingConn, clearPendingConn);
      if (result.connected) deps.scheduleRender();
      return result;
    },
  };
}

function isSameEdge(
  edge: Pick<GraphEdge, 'fromNode' | 'fromPort' | 'toNode' | 'toPort'>,
  other: Pick<GraphEdge, 'fromNode' | 'fromPort' | 'toNode' | 'toPort'>,
): boolean {
  return edge.fromNode === other.fromNode
    && edge.fromPort === other.fromPort
    && edge.toNode === other.toNode
    && edge.toPort === other.toPort;
}
